import { ItemService } from '../catalog';

import { ModelParser } from '../model-parser';
import {
  DataContainer,
  DcuplInitOptions,
  ModelDefinition,
  ListItem,
  RawItem,
  ViewDefinition,
  ChangeDetector,
  trigger,
  DcuplGlobalItemOptions,
  DcuplItemsOptions,
  CallbackFunction,
  DcuplQueryBuilder,
  DcuplQueryGroup,
  DcuplQuery,
  DcuplGlobalQueryOptions,
  generateKey,
  DataOptions,
  DcuplGlobalManyItemOptions,
  AnalyticsController,
  CustomOperatorFn,
  QueryManager,
  PivotResponse,
  DcuplGlobalPivotOptions,
  DcuplGlobalAggregateOptions,
  Aggregation,
  DcuplGlobalSuggestionOptions,
  Suggestion,
  DcuplGlobalGroupByOptions,
  GroupByResponse,
  DcuplGlobalFacetOptions,
  FilterItemEntry,
  ListMetadata,
  DcuplGlobalMetadataOptions,
  CacheController,
  ScriptController,
  perf,
} from '@dcupl/common';
import { ListsController } from './dcupl-list-controller';
import { merge } from 'lodash-es';
import { DcuplList } from '../list';
import { QualityController } from '../quality/quality.controller';

export class DcuplApi<ModelNames extends string = string> {
  protected cdRef = new ChangeDetector();
  protected analyticsController = new AnalyticsController();
  protected scriptController = new ScriptController();
  protected qualityController = new QualityController(this.scriptController);
  protected modelParser = new ModelParser(this.cdRef, this.qualityController, this as any);
  protected globalItemService = new ItemService(this.modelParser, this.scriptController);

  public cacheCtrl = new CacheController(false);

  protected queryManager = new QueryManager(this.scriptController);

  private _loaders = new Map<string, any>();
  private rootQueryBuilder = new DcuplQueryBuilder();
  private _defaultInitOptions: DcuplInitOptions = {
    config: { projectId: undefined, apiKey: undefined, version: 'draft' },
    errorTracking: {
      enabled: false,
    },
    quality: {
      enabled: true,
    },
    analytics: {
      enabled: false,
    },
    performance: {
      cache: {
        enabled: true,
      },
      clone: {
        enabled: true,
      },
    },
    referenceMetadata: {
      enabled: false,
    },
  };

  public initOptions!: DcuplInitOptions;

  public lists: ListsController = new ListsController(this, this.cdRef);

  private busy: boolean = false; // Tracks if the class is busy
  private updateQueue: (() => void)[] = []; // Queue for pending updates

  constructor(options?: DcuplInitOptions) {
    this.analyticsController.init(!!options?.analytics?.enabled);

    this.qualityController.dcupl = this as any;
    this.analyticsController.mark({ name: 'dcupl:constructor' });

    this.initOptions = merge({}, this._defaultInitOptions, options);
    this.qualityController.enabled =
      this.initOptions.errorTracking?.enabled || this.initOptions.quality?.enabled || false;

    this.modelParser.initOptions = Object.assign({}, this.initOptions);

    this.cdRef._init({
      cdRefKey: 'root',
      dcuplInitOptions: this.initOptions,
      analyticsController: this.analyticsController,
    });

    this.cacheCtrl.enabled = !!this.initOptions.performance?.cache?.enabled;
  }

  public models = {
    get: (modelKey: ModelNames): ModelDefinition | undefined => {
      return this.modelParser.models.get(modelKey)?.getModelDefinition();
    },
    set: (model: ModelDefinition<ModelNames>): void => {
      this.modelParser.models.delete(model.key);
      return this.modelParser.addModel(model);
    },
    update: (model: ModelDefinition<ModelNames>): void => {
      return this.modelParser.addModel(model);
    },
    remove: (modelKey: ModelNames): void => {
      this.modelParser.models.delete(modelKey);
    },
    keys: (): string[] => {
      return this.modelParser._getModelKeys();
    },
  };

  public views = {
    get: (viewKey: string): ViewDefinition | undefined => {
      return this.modelParser.views.get(viewKey);
    },
    keys: (options?: { modelKey: string }): string[] => {
      return this.modelParser._getViewKeys(options);
    },
    set: (view: ViewDefinition): void => {
      return this.modelParser.addView(view);
    },
  };

  public data = {
    update: (data: RawItem[], options: DataOptions): void => {
      this.modelParser.addData({
        data,
        model: options.model,
        type: 'update',
        keyProperty: options.keyProperty,
        autoGenerateKey: options.autoGenerateKey,
      });
    },
    upsert: (data: RawItem[], options: DataOptions): void => {
      this.modelParser.addData({
        data,
        model: options.model,
        type: 'upsert',
        keyProperty: options.keyProperty,
        autoGenerateKey: options.autoGenerateKey,
      });
    },
    set: (data: RawItem[], options: DataOptions): void => {
      this.modelParser.addData({
        data,
        model: options.model,
        type: 'set',
        keyProperty: options.keyProperty,
        autoGenerateKey: options.autoGenerateKey,
      });
    },
    remove: (data: RawItem[], options: DataOptions): void => {
      this.modelParser.addData({
        data,
        model: options.model,
        type: 'remove',
        keyProperty: options.keyProperty,
        autoGenerateKey: options.autoGenerateKey,
      });
    },
    reset: (options: { model: string }): void => {
      if (options.model) {
        this.modelParser.models.get(options.model)?.resetData();
        this.modelParser.models.get(options.model)?.resetIndexMap();
      }
    },
    apply: (container: DataContainer<RawItem, ModelNames>): void => {
      return this.modelParser.addData(container);
    },
  };

  public loaders = {
    add: (loader: any, key?: string): void => {
      this.addLoader(loader, key);
    },
    remove: (key: string): void => {
      this.removeLoader(key);
    },
    get: (key: string): any => {
      return this._loaders.get(key);
    },
  };

  public scripts = {
    set: (key: string, script: string): void => {
      this.scriptController.setScript(key, script);
    },
    remove: (key: string): void => {
      this.scriptController.removeScript(key);
    },
    get: (key: string): void => {
      this.scriptController.getScript(key);
    },
    keys: (): string[] => {
      return this.scriptController.keys();
    },
    execute: (key: string, options?: any): any => {
      const args = {
        dcupl: this,
        options,
      };
      return this.scriptController.executeScript(key, 'script', args);
    },
    executeAsync: (key: string, options?: any): any => {
      return new Promise((resolve, reject) => {
        const args = {
          dcupl: this,
          options,
          resolve,
          reject,
        };
        return this.scriptController.executeScript(key, 'async_script', args);
      });
    },
  };

  public fn = {
    pivot: (options: DcuplGlobalPivotOptions): PivotResponse => {
      return this._pivot(options);
    },
    aggregate: (options: DcuplGlobalAggregateOptions): Aggregation => {
      return this._aggregate(options);
    },
    suggest: (options: DcuplGlobalSuggestionOptions): Suggestion[] => {
      return this._suggest(options);
    },
    groupBy: (options: DcuplGlobalGroupByOptions): GroupByResponse => {
      return this._groupBy(options);
    },
    facets: (options: DcuplGlobalFacetOptions): FilterItemEntry[] => {
      return this._facets(options);
    },
    metadata: (options: DcuplGlobalMetadataOptions): ListMetadata => {
      return this._metadata(options);
    },
  };

  @trigger({
    scope: 'global',
    type: 'fn_pivot',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _pivot(options: DcuplGlobalPivotOptions): PivotResponse {
    const list = this.getFnList(options);
    const result = list.catalog.fn.pivot(options.options);
    return result;
  }

  @trigger({
    scope: 'global',
    type: 'fn_aggregate',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _aggregate(options: DcuplGlobalAggregateOptions): Aggregation {
    const list = this.getFnList(options);
    const result = list.catalog.fn.aggregate(options.options);
    return result;
  }

  @trigger({
    scope: 'global',
    type: 'fn_suggest',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _suggest(options: DcuplGlobalSuggestionOptions): Suggestion[] {
    const list = this.getFnList(options);
    const result = list.catalog.fn.suggest(options.options);
    return result;
  }

  @trigger({
    scope: 'global',
    type: 'fn_groupBy',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _groupBy(options: DcuplGlobalGroupByOptions): GroupByResponse {
    const list = this.getFnList(options);
    const result = list.catalog.fn.groupBy(options.options);
    return result;
  }

  @trigger({
    scope: 'global',
    type: 'fn_facets',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _facets(options: DcuplGlobalFacetOptions): FilterItemEntry[] {
    const list = this.getFnList(options);
    const result = list.catalog.fn.facets(options.options);
    return result;
  }

  @trigger({
    scope: 'global',
    type: 'fn_metadata',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _metadata(options: DcuplGlobalMetadataOptions): ListMetadata {
    const list = this.getFnList(options);
    const result = list.catalog.fn.metadata();
    return result;
  }

  protected registerContainer(container: DataContainer): void {
    this.modelParser.addData(container);
  }

  protected updateRegisteredContainer(container: DataContainer): void {
    const relevantContainer = this.modelParser['unprocessedData'].findIndex(
      (c) => c.placeholderUid === container.placeholderUid,
    );
    if (relevantContainer > -1) {
      delete container.placeholderUid;
      this.modelParser['unprocessedData'][relevantContainer] = container;
    }
  }

  protected removeRegisteredContainer(container: DataContainer): void {
    const relevantContainer = this.modelParser['unprocessedData'].findIndex(
      (c) => c.placeholderUid === container.placeholderUid,
    );
    if (relevantContainer > -1) {
      this.modelParser['unprocessedData'].splice(relevantContainer, 1);
    }
  }

  private getFnList(options: { modelKey: string; query?: DcuplGlobalQueryOptions<any> }): DcuplList {
    let list = this.lists.get(`__global_list__${options.modelKey}`);

    if (!list) {
      list = this.lists.create({
        modelKey: options.modelKey,
        listKey: `__global_list__${options.modelKey}`,
      });
    }

    if (options.query) {
      list.catalog.query.apply(options.query, { mode: 'set' });
    } else {
      list.catalog.query.reset();
    }

    return list;
  }

  private updateFnLists(): void {
    for (const list of this.lists.getAll()) {
      if (list.key.startsWith('__global_list__')) {
        list.update();
      }
    }
  }

  public query = {
    one: <ItemType extends ListItem = ListItem>(options: DcuplGlobalItemOptions<ItemType>): ItemType | null => {
      return this.getItem(options);
    },
    many: <ItemType extends ListItem = ListItem>(options: DcuplGlobalManyItemOptions<ItemType>): ItemType[] => {
      return this._getManyItems(options);
    },
    execute: <ItemType extends ListItem = ListItem>(options: DcuplGlobalQueryOptions<ItemType>): ItemType[] => {
      return this.executeQuery(options);
    },
    generate: (
      modelKey: string,
      query: DcuplQueryGroup | DcuplQuery | DcuplQuery[] | DcuplQueryGroup[],
    ): DcuplGlobalQueryOptions<any> => {
      this.rootQueryBuilder.init({ modelKey: modelKey });
      return this.rootQueryBuilder.applyQuery(query);
    },
    registerCustomOperator: (operator: string, fn: CustomOperatorFn): void => {
      this.queryManager.registerCustomOperator(operator, fn);
    },
  };

  public on(cb: CallbackFunction): () => void {
    return this.cdRef.on(cb);
  }

  @trigger({
    scope: 'global',
    type: 'dcupl_initialized',
    connectMessage: true,
  })
  public async init(): Promise<boolean> {
    try {
      await this.performUpdate(() => this.modelParser.init());
      this.cacheCtrl.clear();
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  @trigger({
    scope: 'global',
    type: 'dcupl_updated_manually',
    action: 'update',
    connectMessage: true,
  })
  public async update(): Promise<boolean> {
    try {
      await this.performUpdate(() => this.modelParser.update());
      this.cacheCtrl.clear();
      this.updateFnLists();
      return true;
    } catch (err) {
      console.log(err);
      return false;
    }
  }

  @trigger({
    scope: 'global',
    type: 'dcupl_destroyed',
    connectMessage: true,
  })
  public destroy(): void {
    try {
      this.lists.destroy();
      this._loaders.forEach((loader) => {
        loader.destroy();
      });
    } catch (err) {}
  }

  @trigger({
    scope: 'global',
    type: 'query_one',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private getItem<ItemType = ListItem>(options: DcuplGlobalItemOptions<any>): ItemType | null {
    const model = this.modelParser.models.get(options.modelKey);

    if (!model) {
      throw new Error();
    }

    return this.globalItemService.getItem(options.itemKey, model['data'], model, options) as ItemType | null;
  }

  @trigger({
    scope: 'global',
    type: 'query_many',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private _getManyItems<ItemType = ListItem>(options: DcuplGlobalManyItemOptions<ItemType>): ItemType[] {
    const model = this.modelParser.models.get(options.modelKey);

    if (!model) {
      throw new Error();
    }
    const items = this.globalItemService.getManyItems(options.itemKeys, model['data'], model, options);
    return items as any;
  }

  @trigger({
    scope: 'global',
    type: 'query_execute',
    cloneResult: true,
    connectMessage: true,
    connectMessageResult: true,
  })
  private executeQuery<ItemType extends ListItem = ListItem>(options?: DcuplGlobalQueryOptions<ItemType>): ItemType[] {
    if (!options?.modelKey) {
      throw new Error(`No modelKey provided`);
    }

    const model = this.modelParser.models.get(options?.modelKey);

    if (!model) {
      throw new Error(`Model not found: ${options.modelKey}`);
    }

    const cacheContext = this.cacheCtrl.generateContext({ options });
    const hasCache = this.cacheCtrl.has('query', cacheContext);
    if (hasCache) {
      return this.cacheCtrl.get('query', cacheContext);
    }

    const queriedData = this.queryManager.queryData(model['data'], options, false, model.indicesController['indexMap']);

    const items = this.globalItemService.getItems(queriedData, model, options as DcuplItemsOptions<ListItem>);
    this.cacheCtrl.set('query', cacheContext, items);
    return items as ItemType[];
  }

  private addLoader(loader: any, key?: string): void {
    const start = perf.now();
    const loaderKey = key || generateKey();
    this._loaders.set(loaderKey, loader);
    loader.core = this;
    loader.key = loaderKey;
    const loaderCd = new ChangeDetector();
    loaderCd._init({
      cdRefKey: loaderKey,
      parentCd: this.cdRef,
      dcuplInitOptions: this.initOptions,
      analyticsController: this.analyticsController,
    });
    loader.cdRef = loaderCd;

    if (this.initOptions.config?.projectId) {
      loader.defaultVariables.set('projectId', { key: 'projectId', value: this.initOptions.config?.projectId });
    }
    if (this.initOptions.config?.apiKey) {
      loader.defaultVariables.set('apiKey', { key: 'apiKey', value: this.initOptions.config?.apiKey });
    }
    if (this.initOptions.config?.version) {
      loader.defaultVariables.set('version', { key: 'version', value: this.initOptions.config?.version });
    }

    this.cdRef.trigger({ scope: 'global', type: 'loader_added', connectMessage: true, args: [loaderKey], start });
  }
  @trigger({
    scope: 'global',
    type: 'loader_removed',
    connectMessage: true,
  })
  private removeLoader(key: string): void {
    const loader = this._loaders.get(key);
    if (loader) {
      loader.destroy();
    }
    this._loaders.delete(key);
  }

  /**
   * UPDATE QUEUE
   */

  private async performUpdate(task: () => Promise<void>): Promise<void> {
    if (this.busy) {
      return new Promise((resolve) => {
        this.updateQueue.push(() => task().then(resolve));
      });
    }

    try {
      this.busy = true;
      await task();
    } finally {
      this.busy = false;
      this.processQueue();
    }
  }

  private async processQueue(): Promise<void> {
    if (this.updateQueue.length > 0) {
      const nextTask = this.updateQueue.shift();
      if (nextTask) {
        await nextTask(); // Process the next task in the queue
        this.processQueue();
      }
    }
  }
}
